import React from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import LogoPng from "images/logo.png";
import config from "lib/config";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AppLogoVariant = "dark" | "light" | "color";

export interface AppLogoProps {
  /**
   * "dark"  → texte blanc  + accent orange  (fond sombre)
   * "light" → texte #111   + accent orange  (fond clair)
   * "color" → texte orange + accent #111    (accentuation inversée)
   */
  variant?: AppLogoVariant;
  /** Taille du logo image. Default : "md" (2.5rem) */
  size?: "sm" | "md" | "lg";
  href?: string;
  className?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const IMG_SIZE: Record<NonNullable<AppLogoProps["size"]>, string> = {
  sm: "1.75rem",
  md: "2.5rem",
  lg: "3.25rem",
};

const TEXT_SIZE: Record<NonNullable<AppLogoProps["size"]>, string> = {
  sm: "0.9rem",
  md: "1.15rem",
  lg: "1.45rem",
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Wrapper = styled.a<{ $href?: string }>`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  cursor: ${(p) => (p.$href ? "pointer" : "default")};
  pointer-events: ${(p) => (p.$href ? "auto" : "none")};
`;

const Img = styled.img<{ $size: string }>`
  height: ${(p) => p.$size};
  width: auto;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const Text = styled.h5<{ $textColor: string; $textSize: string }>`
  font-size: ${(p) => p.$textSize};
  font-weight: 900;
  letter-spacing: 0.08em;
  line-height: 1.1;
  color: ${(p) => p.$textColor};
  margin: 0;
`;

const Accent = styled.span<{ $accentColor: string }>`
  color: ${(p) => p.$accentColor};
  display: block;
  letter-spacing: 0.8em;
  font-weight: 600;
`;

// ─── Token map ────────────────────────────────────────────────────────────────

const VARIANT_COLORS: Record<AppLogoVariant, { text: string; accent: string }> =
  {
    dark: { text: "#ffffff", accent: "#ff6c00" },
    light: { text: "#111111", accent: "#ff6c00" },
    color: { text: "#ff6c00", accent: "#111111" },
  };

// ─── Component ────────────────────────────────────────────────────────────────

const AppLogo: React.FC<AppLogoProps> = ({
  variant = "dark",
  size = "md",
  href,
  className,
}) => {
  const { text, accent } = VARIANT_COLORS[variant];

  return (
    <Wrapper
      $href={href}
      href={href}
      className={className}
      aria-label={`${config.appName} ${config.subAppName}`}
    >
      <Img
        src={LogoPng}
        alt={`${config.appName} ${config.subAppName}`}
        $size={IMG_SIZE[size]}
      />
      <Text $textColor={text} $textSize={TEXT_SIZE[size]}>
        {config.appName}
        <Accent $accentColor={accent}>{config.subAppName}</Accent>
      </Text>
    </Wrapper>
  );
};

export default AppLogo;
